import {useDispatch, useSelector} from "react-redux";
import { toggleMobileMenu } from "@/slices/sidebarSlice.ts";
import Logo from "@/components/common/Logo";
import {RootState} from "@/redux/store.ts";
import {Routes} from "@/routes/router.tsx";
import {useNavigate} from "react-router-dom";
import {useState, useRef, useEffect} from "react";
import HelpDrawer from "@/components/Help/HelpDrawer";
import {useTranslation} from "react-i18next";
import ProfileAvatar from "@/components/custom/ProfileAvatar.tsx";
import { useAuth } from "@/hooks/useAuth.tsx";
import useAuthorization from "@/hooks/useAuthorization.tsx";
import useTaskCompletion from "@/hooks/useTaskCompletion.tsx";
import ChatBadge from "@/components/custom/ChatBadge";
import { ChatBubbleLeftRightIcon, QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import notifySrc from "@/assets/images/menu_assets/menu_topbar-icons/topbar_icon-notification-default_desktop+mobile.svg";
import NotificationBadge from "@/components/custom/NotificationBadge";
import OrganisationSwitchModal from "@/components/custom/OrganisationSwitchModal";

const MobileHeader = () => {
    const { t } = useTranslation();
    const { logout } = useAuth();
    const { hasRole, hasLevel } = useAuthorization();
    const { hasIncompleteTasks, completedTasks, totalTasks } = useTaskCompletion();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector((state: RootState) => state.user.user);

    const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
    const [helpInitialTicketId, setHelpInitialTicketId] = useState<string | null>(null);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [isOrgSwitchModalOpen, setIsOrgSwitchModalOpen] = useState(false);
    const userMenuRef = useRef<HTMLDivElement>(null);

    // Close user menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
                setIsUserMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Deep-link bridge: notifications fire this event to open a specific
    // support ticket inside the help drawer's contact tab.
    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent<{ ticketId?: string }>).detail;
            if (detail?.ticketId) {
                setHelpInitialTicketId(detail.ticketId);
                setIsHelpDrawerOpen(true);
            }
        };
        window.addEventListener('openSupportTicket', handler);
        return () => window.removeEventListener('openSupportTicket', handler);
    }, []);

    const handleLogout = async () => {
        try {
            await logout();
            setIsUserMenuOpen(false);
        } catch (error) {
            console.error("Error during logout:", error);
        }
    };

    const handleNavigateToProfile = () => {
        if (user?.firstname && user?.lastname && 
            user.firstname !== "edun" && user.email !== "edun") {
            navigate(Routes.PORTAL_PROFILE);
        } else {
            console.error("Cannot navigate to profile: Invalid user data");
        }
        setIsUserMenuOpen(false);
    };

    const handleNavigateToSubscription = () => {
        navigate(Routes.PORTAL_SUBSCRIPTION);
        setIsUserMenuOpen(false);
    };

    const handleNavigateToTasks = () => {
        navigate(Routes.PORTAL_TASKS);
        setIsUserMenuOpen(false);
    };

    const handleNavigateToSettings = () => {
        navigate(Routes.PORTAL_SETTINGS_DASHBOARD);
        setIsUserMenuOpen(false);
    };

    const handleNavigateToAdmin = () => {
        navigate(Routes.PORTAL_ADMIN_OVERVIEW);
        setIsUserMenuOpen(false);
    };

    const handleOpenOrgSwitchModal = () => {
        setIsOrgSwitchModalOpen(true);
        setIsUserMenuOpen(false);
    };

    const handleNavigateToNotifications = () => {
        navigate(Routes.PORTAL_NOTIFICATIONS);
        setIsUserMenuOpen(false);
    };

    const handleNavigateToChat = () => {
        navigate(Routes.PORTAL_CHAT);
        setIsUserMenuOpen(false);
    };

    const getUserInitials = () => {
        if (user?.firstname && user?.lastname && 
            user.firstname !== "edun" && user.email !== "edun") {
            return `${user.firstname.charAt(0)}${user.lastname.charAt(0)}`;
        }
        return "?";
    };


    return (
        <>
            <header className="sm:hidden flex items-center bg-[#F1EDE7] shadow-md px-4 py-2">
                {/* Logo Section */}
                <Logo
                    alt="Logo"
                    className="w-[120px] h-[40px] object-contain"
                />

                {/* User Profile Section */}
                <div className="ml-auto flex items-center gap-4">
                    {/* Notifications */}
                    {hasLevel('player') && (
                        <div
                            className="flex flex-col items-center cursor-pointer hover:cursor-pointer relative"
                            onClick={handleNavigateToNotifications}
                        >
                            <div className="relative">
                                <img src={notifySrc} width={15} alt="Notifications" />
                                <NotificationBadge />
                            </div>
                            <span className="text-xs text-gray-600">{t('header.menu.notifications')}</span>
                        </div>
                    )}
                    {/* Chat */}
                    <div
                        className="flex flex-col items-center cursor-pointer hover:cursor-pointer relative"
                        onClick={handleNavigateToChat}
                    >
                        <div className="relative">
                            <ChatBubbleLeftRightIcon className="w-4 h-4 text-gray-600" />
                            <ChatBadge />
                        </div>
                        <span className="text-xs text-gray-600">{t('header.menu.chat', 'Chat')}</span>
                    </div>
                    <div
                        className="flex flex-col items-center cursor-pointer hover:cursor-pointer"
                        onClick={() => setIsHelpDrawerOpen(true)}
                    >
                        <QuestionMarkCircleIcon className="w-4 h-4 text-gray-600" />
                        <span className="text-xs text-gray-600">{t('header.menu.help')}</span>
                    </div>
                    {/* User Avatar with Dropdown */}
                    <div className="relative" ref={userMenuRef}>
                        <button
                            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                            className="bg-green-200 h-10 w-10 flex items-center justify-center rounded-full hover:bg-green-300 transition-colors"
                        >
                            <ProfileAvatar
                                initials={getUserInitials()}
                                imageUrl={user?.settings?.avatarPath}
                                size={40}
                            />
                        </button>

                        {/* Dropdown Menu */}
                        {isUserMenuOpen && (
                            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]">
                                {/* User Profile */}
                                <button
                                    onClick={handleNavigateToProfile}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    {t('header.menu.user_profile')}
                                </button>

                                {/* Subscription */}
                                <button
                                    onClick={handleNavigateToSubscription}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    {t('header.menu.subscription')}
                                </button>

                                {/* Tasks */}
                                <button
                                    onClick={handleNavigateToTasks}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                    </svg>
                                    <span className="flex-1">{t('header.menu.tasks')}</span>
                                    {hasIncompleteTasks && (
                                        <span className="text-xs text-red-600 font-medium">
                                            {completedTasks}/{totalTasks}
                                        </span>
                                    )}
                                </button>

                                {/* Settings */}
                                <button
                                    onClick={handleNavigateToSettings}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    {t('header.menu.settings')}
                                </button>

                                {/* Admin BG - Only show for ROLE_ADMIN */}
                                {hasRole('ROLE_ADMIN') && (
                                    <>
                                        <button
                                            onClick={handleNavigateToAdmin}
                                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                            </svg>
                                            {t('header.menu.admin_bg')}
                                        </button>
                                        <button
                                            onClick={handleOpenOrgSwitchModal}
                                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M4 4h7v7H4V4zm9 0h7v7h-7V4zm-9 9h7v7H4v-7zm9 0h7v7h-7v-7z"
                                                />
                                            </svg>
                                            Byt organisation
                                        </button>
                                    </>
                                )}

                                {/* Divider */}
                                <div className="border-t border-gray-100 my-1"></div>

                                {/* Logout */}
                                <button
                                    onClick={handleLogout}
                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                    </svg>
                                    {t('header.menu.logout')}
                                </button>
                            </div>
                        )}
                    </div>
                    {/* Hamburger Icon */}
                    <button
                        onClick={() => dispatch(toggleMobileMenu())}
                        className="flex flex-col space-y-[3px] cursor-pointer"
                        aria-label="Toggle menu"
                    >
                        <div className="w-[21px] h-[2px] bg-black"></div>
                        <div className="w-[21px] h-[2px] bg-black"></div>
                        <div className="w-[21px] h-[2px] bg-black"></div>
                    </button>
                </div>
            </header>
            {/* Help Drawer (page-specific). Deep-links from notifications open
                this drawer with the contact tab + initialTicketId. */}
            <HelpDrawer
                isOpen={isHelpDrawerOpen}
                onClose={() => {
                    setIsHelpDrawerOpen(false);
                    setHelpInitialTicketId(null);
                }}
                mode="page"
                initialTab={helpInitialTicketId ? "contact" : undefined}
                initialTicketId={helpInitialTicketId}
            />

            {/* Organisation Switch Modal - Only for admins */}
            {hasRole('ROLE_ADMIN') && (
                <OrganisationSwitchModal
                    isOpen={isOrgSwitchModalOpen}
                    onClose={() => setIsOrgSwitchModalOpen(false)}
                />
            )}
        </>
    );
};

export default MobileHeader;
